import { PageHeader, Section, Overline, PrimaryCTA, SecondaryCTA } from "@/components/Section";

export default function Why() {
  return (
    <>
      <PageHeader
        overline="The manifesto"
        title="Evidence infrastructure the sector can afford, inspect, and own."
        lede="A short, signed argument for why Continuum exists — and what we refuse to do with it."
        testid="why-page-header"
      />

      <Section testid="why-content" className="!pt-16">
        <div className="grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <Overline className="mb-4">Signed</Overline>
              <p className="text-[#0A1224] font-bold text-lg leading-tight">Chantal Hansen</p>
              <p className="text-slate-500 text-sm mt-1">Founder, The Creative Coefficient</p>
              <div className="mt-8 pt-8 border-t border-slate-200">
                <Overline className="mb-3">Length</Overline>
                <p className="text-slate-600 text-sm tabular-nums">~ 7 minute read</p>
              </div>
              <div className="mt-8">
                <PrimaryCTA to="/contact" testid="why-walkthrough-cta">Book a walkthrough</PrimaryCTA>
              </div>
            </div>
          </aside>

          <article className="lg:col-span-9 prose-continuum max-w-prose">
            <p className="text-2xl md:text-3xl font-black tracking-tighter text-[#0A1224] !mb-10 leading-tight">
              The social sector runs on evidence. The evidence runs on infrastructure most of
              the sector cannot afford, cannot inspect, and does not own.
            </p>

            <p>
              We built Continuum because we kept watching the same pattern. A small agency
              writes a funding application with a story instead of data, because nobody will
              sell them the data layer at a price they can pay. A municipality commissions a
              housing needs report and receives a beautifully-bound PDF that begins ageing the
              day it is delivered. A funder asks the disparity question — does our money land
              where need is? — and nobody has a clean answer.
            </p>

            <p>
              None of those failures are individual failures. They are structural. The sector
              has been served by consulting engagements and proprietary dashboards built for
              someone else's accountability. The continuum of care — housing, mental health,
              aging and disability, health care — has been treated as four different
              software markets, each priced for the largest possible customer.
            </p>

            <h2>What Continuum refuses</h2>
            <ul>
              <li>
                <strong>We will not fabricate.</strong> No invented statistics. No fake client
                logos. No screenshots of features that don't exist. Every number you see on
                this site or in the product carries a source, a method, and a refresh date.
              </li>
              <li>
                <strong>We will not lock data in.</strong> Your data layer stays yours,
                exportable, in formats other tools can read. The moat we want is methodology,
                not export friction.
              </li>
              <li>
                <strong>We will not price the small agency out.</strong> The grant evidence
                packs — priced from $500 — are a deliberate architectural choice, not a
                stripped-down freemium version of the platform.
              </li>
              <li>
                <strong>We will not market against named competitors.</strong> The contrast we
                want to make is made through what Continuum demonstrably is — versioned,
                documented, signed off, Canadian-resident, transparently priced.
              </li>
            </ul>

            <h2>What Continuum commits to</h2>
            <ul>
              <li>
                <strong>Versioned methodology.</strong> When the federal HNA template changes,
                the method version bumps. Outputs carry the version they were produced under.
                Year-over-year comparability is engineered in.
              </li>
              <li>
                <strong>Named accountability.</strong> A real human professional signs every
                statutory output. Not a logo. Not a workflow. A person whose name is on the
                document and on the registry.
              </li>
              <li>
                <strong>Published pricing.</strong> Hiding prices contradicts the principle.
                The tiers, the included scope, and the upgrade paths all live in public.
              </li>
              <li>
                <strong>Canadian data residency.</strong> Public-sector buyers procure against
                residency requirements; we treat that as a feature, not paperwork.
              </li>
            </ul>

            <h2>The democratisation line</h2>

            <blockquote>
              Your community. Your data. Your decisions.
            </blockquote>

            <p>
              That line started as a master pitch. It earned its way down to where it works
              hardest — on the small-agency page. It is what we mean every time we make a
              decision about price, format, or default settings. The smallest organisation we
              serve must be able to use, audit, and leave the platform on its own terms.
            </p>

            <h2>What we are asking</h2>
            <p>
              If you operate anywhere along the continuum of care — agency, council, funder,
              ministry — Continuum is being built for you, and (this part matters) with you.
              The waitlist is not a marketing list. It is a working group. Phase decisions,
              methodology drops, and pricing finalisations are made in public with the people
              who joined first.
            </p>

            <p>
              Join when you're ready. Read the methodology. Ask hard questions in the
              walkthrough. We will answer them the same way we answer the data: with the
              source, the method, and the date.
            </p>

            <p className="!text-[#0A1224] !font-bold !text-lg !leading-snug !mt-12">
              — Chantal Hansen
              <br />
              <span className="font-medium text-slate-500 text-base">Founder, The Creative Coefficient · June 2026</span>
            </p>
          </article>
        </div>
      </Section>

      <Section testid="why-cta" className="bg-[#0A1224] text-white text-center">
        <Overline className="text-slate-400 mb-6">If this matches your principles</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter max-w-3xl mx-auto leading-tight">
          Join the waitlist. Get the methodology drops first.
        </h2>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <PrimaryCTA to="/contact" testid="why-final-cta">Join the waitlist</PrimaryCTA>
          <SecondaryCTA to="/method" dark testid="why-method-cta">Read the method</SecondaryCTA>
        </div>
      </Section>
    </>
  );
}
